import React, { useEffect, useMemo, useState } from 'react';
import {
  Card, Form, Row, Col, Button, InputGroup,
} from 'react-bootstrap';

import { History } from '@material-ui/icons';
import api from '../../api';

import useUserContext from '../../hooks/useUserContext';
import Required from '../Required';
import { state as BST, status as BS } from '../../helpers/buy';
import { isAdmin } from '../../services/rolesSvc';

export default function MainInfo({
  data, onChange, onTypeChange, showHist, readOnly,
}) {
  // #region Get list of fin. sources
  const [finSources, setFinSources] = useState(null);
  useEffect(async () => {
    const sources = await api.get('/mng/finSources');
    const sourcesFb = sources.filter((s) => s.type === 'fb')[0].sources;
    const sourcesSfArray = sources.filter((s) => s.type === 'sf')[0].sources.sf.filter((so) => so.canBuy);
    setFinSources({ ...sourcesFb, sfSource: sourcesSfArray }); // нужны только источники для типа "Грант" или ФОР (внебюджет)
  }, []);
  // #endregion

  const userCtx = useUserContext();

  const finTypes = useMemo(() => (data.projPlans ? [...new Set(data.projPlans.map((p) => p.type))].sort() : []), [data]);
  const years = useMemo(() => (data.projPlans ? [...new Set(data.projPlans.map((p) => p.year).sort())] : []), [data]);

  const isNew = data.buy.year >= 2025;

  const setAvailableAgreements = (year) => (data.projPlans ? [...new Set(data.projPlans.filter((p) => p.year === year).map((p) => p.agreementId))] : []);
  const setAvailableBcCodes = (year) => (data.projPlans
    ? [...new Set(data.projPlans.filter((p) => p.year === year && data.buy.agreementId === p.agreementId).map((p) => p.bcCodeId))]
    : []);
  const setAvailableFunding = (year) => (data.projPlans
    ? [...new Set(data.projPlans.filter((p) => p.year === year && data.buy.agreementId === p.agreementId && data.buy.bcCodeId === p.bcCodeId
                && (data.buy.type !== 'sf' || data.buy.sfSourceId === p.sfSourceId))
      .map((p) => p.fundingId))]
    : []);
  const setAvailableSfSources = (year) => (data.projPlans ? [...new Set(data.projPlans.filter((p) => p.year === year).map((p) => p.sfSourceId))] : []);

  const getSourceTitle = (id, type) => {
    if (id && finSources) {
      const source = finSources[type].find((s) => s.id === id);
      switch (type) {
        case 'agreements':
          return source.num;
        case 'bcCodes':
          return source.fullCode;
        case 'funding':
        case 'sfSource':
          return source.name;
        default:
          return id;
      }
    } else return id;
  };

  // #region Modify items
  const handleBuyYearChange = (year) => {
    onChange(year, 'year');
  };
  const handleSourceChange = (sourceId, type) => {
    onChange(sourceId ? parseInt(sourceId) : null, `${type}Id`);
  };
  // #endregion

  return (
    <Card className="mb-4">
      <Card.Header as={Card.Title} className="p-4">Основная информация</Card.Header>
      <Card.Body className="p-4">
        {finTypes.length > 1
                && (
                <Row className="mb-4">
                  <Form.Group as={Col} md="12">
                    {finTypes.map((type) => (
                      <Form.Check
                        type="radio"
                        name={`typeGroup${type}`}
                        inline
                        label={type === 'fb' ? 'Грант' : 'Софинансирование'}
                        value={type}
                        onChange={(e) => onTypeChange(e.target.value)}
                        checked={data.buy.type === type}
                        key={type}
                        disabled={readOnly || data.buy.state !== 'onProj'}
                      />
                    ))}
                  </Form.Group>
                </Row>
                )}
        <Row className="mb-4">
          <Form.Group as={Col} md="4">
            <Form.Label>
              Год субсидии
              <Required />
            </Form.Label>
            <Form.Control
              required
              as="select"
              value={data.buy.year}
              disabled={readOnly || (BS.approved === data.buy.status || BS.declined === data.buy.status)}
              onChange={(e) => handleBuyYearChange(parseInt(e.target.value))}
            >
              {years.map((y) => <option key={y} value={y}>{y}</option>)}
            </Form.Control>
            <Form.Control.Feedback type="invalid">Выберите год субсидии</Form.Control.Feedback>
          </Form.Group>
          <Form.Group as={Col} md="4">
            <Form.Label>Номер закупки </Form.Label>
            <Form.Control
              value={data.buy.num}
              placeholder="Заполняется автоматически после сохранения"
              readOnly
            />
          </Form.Group>
          <Form.Group as={Col} md="4">
            <Form.Label>Номер версии </Form.Label>
            <InputGroup>
              <Form.Control
                value={data.buy.version}
                readOnly
              />
              <Button variant="outline-primary" onClick={showHist}><History /></Button>
            </InputGroup>
          </Form.Group>
        </Row>
        <Row className="mb-4">
          {data.buy.type === 'fb'
                    && (
                    <Form.Group as={Col} md="4">
                      <Form.Label>
                        Соглашение
                        <Required />
                      </Form.Label>
                      <Form.Control
                        required
                        as="select"
                        value={data.buy.agreementId ?? ''}
                        disabled={readOnly || (BS.approved === data.buy.status || BS.declined === data.buy.status)}
                        onChange={(e) => handleSourceChange(e.target.value, 'agreement')}
                      >
                        <option key="blankChoice" hidden value="" />
                        {setAvailableAgreements(data.buy.year).map((a) => (
                          <option
                            key={a}
                            value={a}
                          >
                            {getSourceTitle(a, 'agreements')}
                          </option>
                        ))}
                      </Form.Control>
                      <Form.Control.Feedback type="invalid">Выберите соглашение</Form.Control.Feedback>
                    </Form.Group>
                    )}
          {data.buy.type === 'fb'
                    && (
                    <Form.Group as={Col} md="4">
                      <Form.Label>
                        Код подраздела БК
                        <Required />
                      </Form.Label>
                      <Form.Control
                        required
                        as="select"
                        value={data.buy.bcCodeId ?? ''}
                        disabled={readOnly || (BS.approved === data.buy.status || BS.declined === data.buy.status)}
                        onChange={(e) => handleSourceChange(e.target.value, 'bcCode')}
                      >
                        <option key="blankChoice" hidden value="" />
                        {setAvailableBcCodes(data.buy.year).map((a) => (
                          <option
                            key={a}
                            value={a}
                          >
                            {getSourceTitle(a, 'bcCodes')}
                          </option>
                        ))}
                      </Form.Control>
                      <Form.Control.Feedback type="invalid">Выберите код подраздела БК</Form.Control.Feedback>
                    </Form.Group>
                    )}
          {data.buy.type === 'sf'
                    && (
                    <Form.Group as={Col} md="4">
                      <Form.Label>
                        Источник
                        <Required />
                      </Form.Label>
                      <Form.Control
                        required
                        as="select"
                        value={data.buy.sfSourceId ?? ''}
                        disabled={readOnly || (BS.approved === data.buy.status || BS.declined === data.buy.status)}
                        onChange={(e) => handleSourceChange(e.target.value, 'sfSource')}
                      >
                        <option key="blankChoice" hidden value="" />
                        {setAvailableSfSources(data.buy.year).map((a) => (
                          <option
                            key={a}
                            value={a}
                          >
                            {getSourceTitle(a, 'sfSource')}
                          </option>
                        ))}
                      </Form.Control>
                      <Form.Control.Feedback type="invalid">Выберите тип финансирования</Form.Control.Feedback>
                    </Form.Group>
                    )}
          {isNew ? '' : (
            <Form.Group as={Col} md="4">
              <Form.Label>
                Тип {data.buy.type === 'sf' ? 'co' : ''}финансирования
                <Required />
              </Form.Label>
              <Form.Control
                required
                as="select"
                value={data.buy.fundingId ?? ''}
                disabled={readOnly || (BS.approved === data.buy.status || BS.declined === data.buy.status)}
                onChange={(e) => handleSourceChange(e.target.value, 'funding')}
              >
                <option key="blankChoice" hidden value="" />
                {setAvailableFunding(data.buy.year).map((a) => <option key={a} value={a}>{getSourceTitle(a, 'funding')}</option>)}
              </Form.Control>
              <Form.Control.Feedback type="invalid">Выберите тип финансирования</Form.Control.Feedback>
            </Form.Group>
          )}
        </Row>
        <Row>
          <Form.Group as={Col}>
            <Form.Label>
              Название
              <Required />
            </Form.Label>
            <Form.Control
              placeholder="Введите название закупки"
              required
              as="textarea"
              rows={3}
              value={data.buy.name}
              readOnly={readOnly || (BST.none === data.buy.state && !isAdmin(userCtx))}
              onChange={(elem) => onChange(elem.target.value, 'name')}
            />
            <Form.Control.Feedback type="invalid">Обязательное поле к заполнению</Form.Control.Feedback>
          </Form.Group>
        </Row>
      </Card.Body>
    </Card>
  );
}

const cardHeaderStyling = {
  display: 'flex', justifyContent: 'end', minHeight: 38,
};
